import React from 'react';
import PropTypes from 'prop-types';
import anime from 'animejs';

import AnalyticsLogger from '../../../../helpers/analytics/analyticsLogger';
import TextMap from '../../../../helpers/TextMap';
import dataModel from '../../../../models/DataModel';
import UserAvatar from '../../../../components/UserAvatar';
import ExpandableSection from '../../../../components/ExpandableSection';

import MultipleUsersIcon from '../../../../components/Iconoclast/MultipleUsersIcon';
import styles from '../../../../assets/styles/globals.scss';
import * as BuildOwnerPermissionsUtil from '../../../../helpers/permissions/BuildOwnerPermissionsUtil';
import './MobileAssignmentTab.scss';

export class MobileAssignmentTab extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      availableUsers: [],
      fieldUsers: props.fieldUsers || [],
      isLoading: true,
      canBuildOwnerAssign: false,
    };
    MobileAssignmentTab.propTypes = {
      fieldUsers: PropTypes.arrayOf(PropTypes.object),
      task: PropTypes.object.isRequired,
      project: PropTypes.object.isRequired,
      isBuildOwner: PropTypes.bool,
      parentCompanyId: PropTypes.number,
    };
    this.textMap = new TextMap();

    // track set of field user IDs
    const fieldUserIds = this.state.fieldUsers.map(user => user.id);
    this.fieldUserIds = new Set(fieldUserIds);

    // animation constants/values
    this.contractedSectionHeight = 45;
    this.userCardHeight = 71;
    this.fieldUserCardsHeight = this.userCardHeight * this.state.fieldUsers.length;
    this.fieldUserSectionHeight = this.fieldUserCardsHeight + this.contractedSectionHeight;

    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.handleFieldUserExpand = this.handleFieldUserExpand.bind(this);
    this.addUser = this.addUser.bind(this);
    this.removeUser = this.removeUser.bind(this);
  }

  componentDidMount() {
    const { isBuildOwner } = this.props;

    if (isBuildOwner) {
      this.canBuildOwnerAssign();
    }
    this.updateProjectUsers();
  }

  componentDidUpdate(prevProps) {
    const { fieldUsers, isBuildOwner } = this.props;
    const fieldUsersChanged = this.didFieldUsersChange(fieldUsers);

    if (fieldUsersChanged) {
      // update heights for animations
      this.fieldUserCardsHeight = this.userCardHeight * fieldUsers.length;
      this.fieldUserSectionHeight = this.fieldUserCardsHeight + this.contractedSectionHeight;
      // update state
      this.setState({ isLoading: true, availableUsers: [], fieldUsers });
      
      if (isBuildOwner) {
        this.canBuildOwnerAssign();
      }
      this.updateProjectUsers();
    }
  }

  didFieldUsersChange(newFieldUsers) {
    if (newFieldUsers) {
      const newFieldUserIds = newFieldUsers.map(user => user.id);
      if (newFieldUserIds.length != this.fieldUserIds.size) {
        this.fieldUserIds = new Set(newFieldUserIds);
        return true;
      }
      else {
        for (let userId of newFieldUserIds) {
          if (!this.fieldUserIds.has(userId)) {
            this.fieldUserIds = new Set(newFieldUserIds);
            return true;
          }
        }  
      }
    }
    return false;
  }

  updateProjectUsers() {
    const { project } = this.props;
    if (project.users && project.users.length) {  
      const availableUsers = [];
      for (let user of project.users) {
        if (!this.isUserAssignedToTask(user.id)) {
          availableUsers.push(user);
        }
      }
      this.setState({ availableUsers });
    }
  }

  canBuildOwnerAssign() {
    const { fieldUsers, parentCompanyId } = this.props;

    this.setState({ canBuildOwnerAssign: BuildOwnerPermissionsUtil.canBuildOwnerAssign(fieldUsers, parentCompanyId) });
  }

  isUserAssignedToTask(selectedUserId) {
    const { task } = this.props;

    //check if that user already has an assignment
    const usersAssignments = task.user_task_assignments.filter(uta => {
      return uta.user_id == selectedUserId;
    });
    return usersAssignments.length > 0;

  }

  showUserModal(handleOwnerOkFunction, user) {
    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: 'You have selected one or more users who are not part of your company. Would you like to continue?',
      cancelButtonText: 'NO',
      okButtonText: 'CONTINUE',
      width: 320,
      height: 182,
      handleOwnerOk: () =>  {
        handleOwnerOkFunction(user);
      },
    });
  }

  handleUserChange(isButtonAdd, user) {
    const { isBuildOwner } = this.props;
    
    //check if build owner and added user is not in build owner company
    const saveHandler = isButtonAdd ? this.addUser : this.removeUser;
    if (isBuildOwner && user.companies[0].id != dataModel.usersDAO.currentUser.companies[0].id) {
      //show modal
      this.showUserModal(saveHandler, user);
    } 
    else {
      saveHandler(user);
    }
  }

  async addUser(user) {
    if (user && user.id && !this.addInProgress) {
      this.addInProgress = true;
      this.analyticsLogger.logTaskAdminEvent('TaskAssigned - Assigned To');

      // Ensure we don't modify objects in state
      const availableUsers = [...this.state.availableUsers];
      const fieldUsers = [...this.state.fieldUsers];
      
      const availableUserIndex = availableUsers.findIndex(availableUser => availableUser.id == user.id);
      availableUsers.splice(availableUserIndex, 1);
      fieldUsers.push(user);
      //track index in case another change is made during request
      const addedUserIndex = fieldUsers.length - 1; 
      
      dataModel.tasksDAO.addFieldUserToTask(user, this.props.task).then(response => {
        if (response && response.error) {
          // roll back UI changes
          availableUsers.push(user);
          fieldUsers.splice(addedUserIndex, 1);
          this.fieldUserCardsHeight -= this.userCardHeight;
          this.fieldUserSectionHeight -= this.userCardHeight;
          this.setState({ availableUsers, fieldUsers });
        }
      });

      this.fieldUserCardsHeight += this.userCardHeight;
      this.fieldUserSectionHeight += this.userCardHeight;
      await this.animateAvailableUsers().finished;
      this.setState({ availableUsers, fieldUsers }, () => this.addInProgress = false);

    }
  }

  async removeUser(user) {
    if (user && user.id && !this.addInProgress) {
      this.addInProgress = true;
      // Ensure we don't modify objects in state
      const availableUsers = [...this.state.availableUsers];
      const fieldUsers = [...this.state.fieldUsers];
      
      const fieldUserIndex = fieldUsers.findIndex(fieldUser => fieldUser.id == user.id);
      fieldUsers.splice(fieldUserIndex, 1);
      availableUsers.push(user);
      //track index in case another change is made during request
      const removedUserIndex = availableUsers.length - 1;
    
      dataModel.tasksDAO.removeFieldUserFromTask(user, this.props.task).then(response => {
        if (response && response.error) {
          // roll back UI changes
          fieldUsers.push(user);
          availableUsers.splice(removedUserIndex, 1);
          this.fieldUserCardsHeight += this.userCardHeight;
          this.fieldUserSectionHeight += this.userCardHeight;
          this.setState({ availableUsers, fieldUsers });
        }
      });

      const userFadeOutAnimation = anime({
        targets: '#user-'+user.id,
        height: 0,
        padding: 0,
        easing: 'linear',
        duration: 300,
      });
      await userFadeOutAnimation.finished;

      this.fieldUserCardsHeight -= this.userCardHeight;
      this.fieldUserSectionHeight -= this.userCardHeight;
      await this.animateAvailableUsers().finished;
      
      this.setState({ availableUsers, fieldUsers }, () => this.addInProgress = false);
    }
  }

  animateAvailableUsers(duration) {
    return anime({
      targets: '#available-users-container',
      top: this.fieldUserSectionHeight,
      easing: 'easeInOutCubic',
      duration: duration || 500,
    });
  }

  handleFieldUserExpand(isExpanded) {
    if (isExpanded) {
      this.fieldUserSectionHeight += this.fieldUserCardsHeight;
    }
    else this.fieldUserSectionHeight -= this.fieldUserCardsHeight;
    this.animateAvailableUsers();
  }

  userDisplay(unexpandedUser, isButtonAdd) {
    const { isBuildOwner, parentCompanyId } = this.props;

    let user = unexpandedUser;
    
    let showButton = true;
    //allow build owners to add/remove only vendor users (turn off button for all parent users)
    if ((isBuildOwner && user.companies[0].id == parentCompanyId) || (isBuildOwner && !this.state.canBuildOwnerAssign)) {
      showButton = false;
    }

    return (
      <div className='assigned-to-user' id={ 'user-'+user.id } key={ 'user-'+user.id } >
        <UserAvatar user={ user } extraLarge={true} />
        <div className='user-info'>
          <h2>
            { `${user.first_name.toUpperCase()} ${user.last_name.toUpperCase()}` }
          </h2>
          <p>
            { `${user.companies[0].name}` }
          </p>
        </div>
        {
          showButton == true &&  
          <button className='user-button' onClick={ () => this.handleUserChange(isButtonAdd, user) }>
            { isButtonAdd ? this.textMap.add.toUpperCase() : this.textMap.remove.toUpperCase() }
          </button>
        }
      </div>
    );
  }

  sortByName(a,b) {
    const aName = a.first_name.toUpperCase() + ' ' + a.last_name.toUpperCase();
    const bName = b.first_name.toUpperCase() + ' ' + b.last_name.toUpperCase();
    if (aName < bName)
      return -1;
    if (aName > bName)
      return 1;
    return 0;
  }

  render() {
    const { fieldUsers, availableUsers } = this.state;
    if (fieldUsers && fieldUsers.length) { fieldUsers.sort(this.sortByName); }
    if (availableUsers && availableUsers.length) { availableUsers.sort(this.sortByName); }

    return (
      <div className='MobileAssignmentTab'>
        <div className='assignment-container'>
        {
          fieldUsers.length > 0 &&
          
            <ExpandableSection initiallyExpanded={true} numberUsers={fieldUsers.length!=0?fieldUsers.length:0} name={ this.textMap.assignedTo.toUpperCase() + ` (${fieldUsers.length})`} onExpand={this.handleFieldUserExpand}>
              {
                fieldUsers.map((user) => {
                  return this.userDisplay(user, false);
                })
              }
            </ExpandableSection>
          
        }
        </div>
        <div className='assignment-container' id='available-users-container' style={{ position: 'absolute', top: this.fieldUserSectionHeight }}>
            <ExpandableSection initiallyExpanded={true} name={ this.textMap.availableForAssignment.toUpperCase()} numberUsers={availableUsers.length!=0?availableUsers.length:0}>
              {
                availableUsers.length > 0 && availableUsers.map((user) => {
                  return this.userDisplay(user, true);
                })
              }
              {
                availableUsers.length == 0 && (
                  <div className='assignment-container__empty'>
                    <MultipleUsersIcon iconColor={styles.lightElement} />
                    <p>There are no available project members. Navigate to projects and add members in the web application.</p>
                  </div>
                )
              }
            </ExpandableSection>
        </div>
      </div>
    );
  }

}

export default MobileAssignmentTab;



// WEBPACK FOOTER //
// ./src/containers/TasksContainer/MobileTaskDetails/MobileAssignmentTab/index.js